import Routes from "constants/routes";
import { createBrowserRouter, RouteObject } from "react-router-dom";

const toRoute = (e: JSX.Element) => ({ element: e });

const lazyDashboardPage = () => import("pages/Dashboard/DashboardPage");

const lazyEventDetailPage = () =>
  import("pages/Host/HostDashboardEventAndPromotionDetailPage")
    .then(({ Component }) => <Component type="Event" />)
    .then(toRoute);

const lazyPromotionDetailPage = () =>
  import("pages/Host/HostDashboardEventAndPromotionDetailPage")
    .then(({ Component }) => <Component type="Promotion" />)
    .then(toRoute);

const lazyBuildUpStatusDetailPage = () =>
  import("pages/BuildUp/BuildUpStatusPage").then(({ Component }) => <Component />).then(toRoute);

const lazyFreePlayTodayPage = () =>
  import("pages/BuildUp/FreePlayTodayPage").then(({ Component }) => <Component />).then(toRoute);

const lazyPlayersOfInterestPage = () =>
  import("pages/Host/PlayersOfInterestPage").then(({ Component }) => <Component />).then(toRoute);

const dashboardRoutes: RouteObject[] = [
  {
    index: true,
    lazy: lazyDashboardPage,
  },
  {
    path: Routes.dashboard,
    lazy: lazyDashboardPage,
    children: [
      {
        path: Routes.viewHostDashboardEvent,
        lazy: lazyEventDetailPage,
      },
      {
        path: Routes.viewHostDashboardPromotion,
        lazy: lazyPromotionDetailPage,
      },
      {
        path: Routes.viewBuildUpStatusDashboard,
        lazy: lazyBuildUpStatusDetailPage,
      },
      {
        path: Routes.viewFreePlayTodayDashboard,
        lazy: lazyFreePlayTodayPage,
      },
      {
        path: Routes.viewFreePlayTodayDashboard,
        lazy: lazyFreePlayTodayPage,
      },
    ],
  },
  {
    path: Routes.activeUnHostedPlayersOnFloor,
    lazy: () => import("pages/Host/ActiveUnHostedPlayersOnFloorPage"),
  },
  {
    path: Routes.activePlayersOnFloor,
    lazy: () => import("pages/Host/ActivePlayersOnFloorPage"),
  },
  {
    path: Routes.activeReservations,
    lazy: () =>
      import("pages/Host/ReservationsPage").then(({ Component }) => <Component status="active" />).then(toRoute),
  },
  {
    path: Routes.upcomingReservations,
    lazy: () =>
      import("pages/Host/ReservationsPage").then(({ Component }) => <Component status="upcoming" />).then(toRoute),
  },
  {
    path: Routes.hostEvents,
    lazy: () => import("pages/Host/HostEventsPage"),
    children: [
      {
        path: Routes.viewHostEvent,
        lazy: lazyEventDetailPage,
      },
    ],
  },
  {
    path: Routes.hostPromotions,
    lazy: () => import("pages/Host/HostPromotionsPage"),
    children: [
      {
        path: Routes.viewHostPromotion,
        lazy: lazyPromotionDetailPage,
      },
    ],
  },
  {
    path: Routes.guestAndHostAudit,
    lazy: () => import("pages/Host/GuestAndHostAuditPage"),
  },
  {
    path: Routes.tripsIncreasing,
    lazy: () =>
      import("pages/Host/HostTripPage").then(({ Component }) => <Component type="increasing" />).then(toRoute),
  },
  {
    path: Routes.tripsDecreasing,
    lazy: () =>
      import("pages/Host/HostTripPage").then(({ Component }) => <Component type="decreasing" />).then(toRoute),
  },
  {
    path: Routes.trendingCustomerValues,
    lazy: () => import("pages/Host/TrendingCustomerScoresPage"),
  },
  {
    path: Routes.birthdays,
    lazy: () => import("pages/Host/PlayerBirthdaysPage"),
  },
  {
    path: Routes.viewBuildUpPlayers,
    lazy: () => import("pages/BuildUp/BuildUpPlayersPage"),
    children: [
      {
        path: Routes.viewBuildUpStatusPage,
        lazy: lazyBuildUpStatusDetailPage,
      },
      {
        path: Routes.viewFreePlayTodayPage,
        lazy: lazyFreePlayTodayPage,
      },
    ],
  },
  {
    path: Routes.playersOfInterest,
    lazy: lazyPlayersOfInterestPage,
  },
];

export const playerRoutes: RouteObject[] = [
  {
    path: Routes.player,
    lazy: () => import("pages/Player/PlayerPage"),
    children: [
      {
        index: true,
        lazy: () => import("pages/Player/OverviewPage"),
      },
      {
        path: Routes.overview,
        lazy: () => import("pages/Player/OverviewPage"),
      },
      {
        path: Routes.notes,
        lazy: () => import("pages/Player/NotesPage"),
        children: [
          {
            path: Routes.viewNote,
            lazy: () => import("pages/Notes/NoteDetailPage"),
          },
        ],
      },
      {
        path: Routes.credits,
        lazy: () => import("pages/Player/CreditsPage"),
      },
      {
        path: Routes.offers,
        lazy: () => import("pages/Player/OffersPage"),
      },
      {
        path: Routes.trips,
        lazy: () => import("pages/Player/TripsPage"),
      },
      {
        path: Routes.comps,
        lazy: () => import("pages/Player/CompsPage"),
      },
      {
        path: Routes.stays,
        lazy: () => import("pages/Player/Stays/StaysPage"),
        children: [
          {
            index: true,
            lazy: () => import("pages/Player/Stays/CurrentStaysPage"),
          },
          {
            path: Routes.currentStays,
            lazy: () => import("pages/Player/Stays/CurrentStaysPage"),
          },
          {
            path: Routes.upcomingStays,
            lazy: () => import("pages/Player/Stays/UpcomingStaysPage"),
          },
          {
            path: Routes.previousStays,
            lazy: () => import("pages/Player/Stays/PreviousStaysPage"),
          },
          {
            path: Routes.viewStay,
            lazy: () => import("pages/Player/Stays/ReservationDetailsPage"),
          },
          {
            path: Routes.cancelledStays,
            lazy: () => import("pages/Player/Stays/CancelledStaysPage"),
          },
        ],
      },
      {
        path: Routes.booking,
        lazy: () => import("pages/Player/BookingPage"),
      },
      {
        path: Routes.mobileApp,
        lazy: () => import("pages/Player/MobileAppPage"),
      },
      {
        path: Routes.netRevenue,
        lazy: () => import("pages/Player/NetRevenuePage"),
      },
      {
        path: Routes.eventsAndPromotions,
        lazy: () => import("pages/Player/EventsAndPromosPage"),
      },
    ],
  },
];

export const createAppRouter = (base: JSX.Element) => {
  return createBrowserRouter([
    {
      path: Routes.root,
      element: base,
      children: [
        ...dashboardRoutes,
        ...playerRoutes,
        {
          path: Routes.playerSearch,
          lazy: () => import("pages/Player/PlayerSearchPage"),
        },
        {
          path: Routes.pageNotFound,
          lazy: () => import("pages/NotFound/PageNotFoundPage"),
        },
        {
          path: Routes.whatsNew,
          lazy: () => import("pages/WhatsNewPage"),
        },
        {
          path: Routes.unauthorized,
          lazy: () => import("pages/Unauthorized/UnauthorizedPage"),
        },
      ],
    },
  ]);
};
